import React from "react";

import { Box, Divider, Stack, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import CustomButton from "../input/CustomButton";

import { isEmpty } from "../../helper-functions/shared/miscUtils";

/**
 *  Stacked header featuring:
 * - title
 * - subtitle
 * - icon(s), incl. tooltip and click event
 * - button(s), incl. icon, title and click event
 *
 * Props format:
 * - obj: [{icon: </>, title: "", onClick: ()=>{} }]
 */
export default function StackedHeader(props) {
    const { title, subtitle } = props;
    const { icons = [], buttons = [] } = props;

    const isMobile = props.browser.isMobile;
    const screenWidth = props.browser.screenWidth;
    const openDrawer = props.sidebar.openAppDrawer;

    const renderIcon = (obj, index) => {
        // Skip rendering if the icon should not be visible
        if (obj?.visible && !obj.visible(props)) return null;

        return <CustomButton {...props} key={index} icon={obj?.icon} onClick={obj?.onClick} tooltip={obj?.tooltip} disabled={obj?.disabled} />;
    };

    const renderButton = (obj, index) => {
        // Skip rendering if the button should not be visible
        if (obj?.visible && !obj.visible(props)) return null;

        return (
            <CustomButton
                {...props}
                key={index}
                startIcon={obj?.icon}
                onClick={obj?.onClick}
                title={obj?.title}
                tooltip={obj?.tooltip}
                disabled={obj?.disabled}
                variant={obj?.variant}
                size={obj?.size}
                className={obj?.className}
            />
        );
    };

    const MobileIcons = ({ icons }) => {
        const [anchorEl, setAnchorEl] = React.useState(null);
        const open = Boolean(anchorEl);

        const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const handleItemClick = (onClick) => {
            onClick();
            handleClose();
        };

        return (
            <>
                <CustomButton icon={<MoreVertIcon />} onClick={handleClick} tooltip="More options" />

                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    {icons.map((obj, index) => {
                        // Skip rendering if the icon should not be visible
                        if (obj?.visible && !obj.visible(props)) return null;

                        return (
                            <MenuItem key={index} onClick={() => handleItemClick(obj.onClick)} disabled={obj?.disabled} dense>
                                {obj.icon}
                                {obj.tooltip && <Typography sx={{ ml: 1 }}>{obj.tooltip}</Typography>}
                            </MenuItem>
                        );
                    })}
                </Menu>
            </>
        );
    };

    return (
        <Box mb={1}>
            <Stack m={0.5} mb={0} justifyContent="space-between" alignItems="center" direction="row">
                <Stack direction="row" m={0} p={0}>
                    <Typography mr={2} variant="h5">
                        {title}
                    </Typography>

                    {!isEmpty(icons) && <Divider sx={{ m: 0, mr: 2 }} orientation="vertical" variant="middle" flexItem />}

                    {(isMobile || screenWidth < 600 || (openDrawer && screenWidth < 800)) && icons.length > 1
                        ? MobileIcons({ icons })
                        : icons.map((obj, index) => renderIcon(obj, index))}
                </Stack>

                <Stack direction="row" m={0} p={0}>
                    {buttons.map((obj, index) => renderButton(obj, index))}
                </Stack>
            </Stack>

            {subtitle && (
                <Stack m={0} ml={0.5} justifyContent="space-between" alignItems="center" direction="row">
                    <Typography className="sm-font italic pre-line" mr={2}>
                        {subtitle}
                    </Typography>
                </Stack>
            )}
        </Box>
    );
}
